import RBush from "rbush";
import { CandidateTrajectory } from "./CandidateTrajectory.js";
import { EndTrajectory } from "./EndTrajectory.js";
import { FullTrajectory } from "./FullTrajectory.js";
import { PointCandidateTrajectory } from "./PointCandidateTrajectory.js";
import { RTreeID } from "./RTreeID.js";
import { cloneRBush } from "../utils/RBushCloner.js";
export { ActiveGraph };
class ActiveGraph {
    constructor({ waypoints, waypointsTree, tangents, tangentsTree }) {
        this.waypoints = waypoints;
        this.waypointsTree = waypointsTree;
        this.tangents = tangents;
        this.tangentsTree = tangentsTree;
    }
    clone() {
        const tangentMap = new Map();
        const trajectoryMap = new Map();
        const cloned = new ActiveGraph({
            waypoints: new Map(),
            waypointsTree: cloneRBush(this.waypointsTree),
            tangents: new Map(),
            tangentsTree: cloneRBush(this.tangentsTree),
        });
        this.waypoints.forEach((waypoint, id) => {
            cloned.waypoints.set(id, waypoint.clone());
        });
        cloned.waypoints.forEach((waypoint, id) => {
            waypoint.trajectories = waypoint.trajectories.map(trajectory => {
                const c = trajectory.clone();
                c.waypoint = waypoint;
                trajectoryMap.set(trajectory, c);
                return c;
            });
        });
        for (let id of this.waypoints.keys()) {
            const oldWaypoint = this.waypoints.get(id);
            const clonedWaypoint = cloned.waypoints.get(id);
            for (let j = 0; j < oldWaypoint.trajectories.length; j++) {
                const oldTrajectory = oldWaypoint.trajectories[j];
                const clonedTrajectory = clonedWaypoint.trajectories[j];
                if (oldTrajectory instanceof CandidateTrajectory && clonedTrajectory instanceof CandidateTrajectory) {
                    if (!tangentMap.has(oldTrajectory.tangent)) {
                        clonedTrajectory.tangent = oldTrajectory.tangent.clone();
                        tangentMap.set(oldTrajectory.tangent, clonedTrajectory.tangent);
                    }
                    else {
                        clonedTrajectory.tangent = tangentMap.get(oldTrajectory.tangent);
                    }
                    if (clonedTrajectory.tangent.isSource(oldTrajectory)) {
                        clonedTrajectory.tangent.sourceTrajectory = clonedTrajectory;
                    }
                    else if (clonedTrajectory.tangent.isTarget(oldTrajectory)) {
                        clonedTrajectory.tangent.targetTrajectory = clonedTrajectory;
                    }
                    else {
                        throw new Error('Unexpected case');
                    }
                }
                else if (oldTrajectory instanceof PointCandidateTrajectory && clonedTrajectory instanceof PointCandidateTrajectory) {
                    continue;
                }
                else if (oldTrajectory instanceof FullTrajectory && clonedTrajectory instanceof FullTrajectory) {
                    for (let k = 0; k < clonedTrajectory.tangents.length; k++) {
                        if (!tangentMap.has(oldTrajectory.tangents[k])) {
                            clonedTrajectory.tangents[k] = oldTrajectory.tangents[k].clone();
                            tangentMap.set(oldTrajectory.tangents[k], clonedTrajectory.tangents[k]);
                        }
                        else {
                            clonedTrajectory.tangents[k] = tangentMap.get(oldTrajectory.tangents[k]);
                        }
                        if (clonedTrajectory.tangents[k].isSource(oldTrajectory)) {
                            clonedTrajectory.tangents[k].sourceTrajectory = clonedTrajectory;
                        }
                        else if (clonedTrajectory.tangents[k].isTarget(oldTrajectory)) {
                            clonedTrajectory.tangents[k].targetTrajectory = clonedTrajectory;
                        }
                        else {
                            throw new Error('Unexpected case');
                        }
                    }
                }
                else if (oldTrajectory instanceof EndTrajectory && clonedTrajectory instanceof EndTrajectory) {
                    if (!tangentMap.has(oldTrajectory.tangent)) {
                        clonedTrajectory.tangent = oldTrajectory.tangent.clone();
                        tangentMap.set(oldTrajectory.tangent, clonedTrajectory.tangent);
                    }
                    else {
                        clonedTrajectory.tangent = tangentMap.get(oldTrajectory.tangent);
                    }
                    if (clonedTrajectory.tangent.isSource(oldTrajectory)) {
                        clonedTrajectory.tangent.sourceTrajectory = clonedTrajectory;
                    }
                    else if (clonedTrajectory.tangent.isTarget(oldTrajectory)) {
                        clonedTrajectory.tangent.targetTrajectory = clonedTrajectory;
                    }
                    else {
                        throw new Error('Unexpected case');
                    }
                }
            }
        }
        cloned.tangents = new Map([...tangentMap.entries()].map(e => [e[1].id, e[1]]));
        return {
            activeGraph: cloned,
            trajectoryMap,
            tangentMap
        };
    }
    updateWaypoint(waypoint, oldWaypoint) {
        this.waypoints.set(waypoint.id, waypoint);
        const newID = new RTreeID({
            id: waypoint.id,
            minX: waypoint.minX,
            maxX: waypoint.maxX,
            minY: waypoint.minY,
            maxY: waypoint.maxY
        });
        const removeID = oldWaypoint !== undefined ? new RTreeID({
            id: oldWaypoint.id,
            minX: oldWaypoint.minX,
            maxX: oldWaypoint.maxX,
            minY: oldWaypoint.minY,
            maxY: oldWaypoint.maxY
        }) : new RTreeID({
            id: waypoint.id,
            minX: -Infinity,
            maxX: Infinity,
            minY: -Infinity,
            maxY: Infinity
        });
        this.waypointsTree.remove(removeID, (a, b) => a.id === b.id);
        this.waypointsTree.insert(newID);
    }
    addTangent(tangent) {
        this.tangents.set(tangent.id, tangent);
        this.tangentsTree.insert(new RTreeID({
            id: tangent.id,
            minX: tangent.minX,
            maxX: tangent.maxX,
            minY: tangent.minY,
            maxY: tangent.maxY
        }));
    }
    removeTangent(tangent) {
        this.tangents.delete(tangent.id);
        this.tangentsTree.remove(new RTreeID({
            id: tangent.id,
            minX: tangent.minX,
            maxX: tangent.maxX,
            minY: tangent.minY,
            maxY: tangent.maxY
        }), (a, b) => a.id === b.id);
    }
    static create() {
        return new ActiveGraph({
            waypoints: new Map(),
            waypointsTree: new RBush(),
            tangents: new Map(),
            tangentsTree: new RBush()
        });
    }
}
//# sourceMappingURL=ActiveGraph.js.map